import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject, ChangeDetectorRef, signal, computed } from '@angular/core';

import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { TelecomService } from '@generated/services/telecom.service';
import { AgentsService } from '@generated/services/agents.service';
import { PhoneNumberAgentResult } from '@generated/models/phone-number-agent-result';
import { AgentSummary } from '@generated/models/agent-summary';
import { OpenApiPropertiesSchema } from '@generated/models/open-api-properties-schema';
import { CallDto } from '@generated/models/call-dto';
import { CallStatus } from '@generated/models/call-status';
import { EndReason } from '@generated/models/end-reason';
import { NotificationService } from '@core/services/notification.service';
import { Signalr } from '@core/services/signalr';
import { Subject, takeUntil } from 'rxjs';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { LiveListenButtonComponent } from '@shared/components/live-listen-button/live-listen-button.component';
import { PropertiesEdit } from '@shared/components/properties-edit/properties-edit';
import { descriptorEntriesToDict, dictToValueEntries } from '@shared/utils/openapi-schema-utils';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';

@Component({
  selector: 'app-phone-number-edit',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective,
    FormsModule,
    DatePipe,
    ButtonModule,
    CardModule,
    SelectModule,
    ProgressSpinnerModule,
    BreadcrumbModule,
    ToolbarModule,
    TooltipModule,
    DividerModule,
    MenuModule,
    InputTextModule,
    TagModule,
    DeleteConfirmationComponent,
    LiveListenButtonComponent,
    PropertiesEdit,
],
  templateUrl: './phone-number-edit.html',
  styleUrl: './phone-number-edit.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneNumberEditComponent implements OnInit, OnDestroy {
  private telecomService = inject(TelecomService);
  private agentsService = inject(AgentsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private signalrService = inject(Signalr);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  phoneNumberAgent: PhoneNumberAgentResult | null = null;
  agents: AgentSummary[] = [];
  provider: string = '';

  selectedAgentId: string | null = null;

  isLoading = false;
  isLoadingAgents = false;
  agentsLoadError = false;
  isSaving = false;
  isDeleting = false;
  deleteDialogVisible = false;
  isEditingLabel = false;
  editedLabel = '';

  // Test call
  testCallTargetNumber = '';
  isTestingCall = false;

  // Переменные агента — редактируем перед звонком тем же компонентом,
  // что и additional_properties у моделей (PropertiesEdit).
  readonly variablesSchema = signal<OpenApiPropertiesSchema | null>(null);
  readonly variableValues = signal<Record<string, any>>({});
  readonly variablesValid = signal<boolean>(true);
  readonly variableSchemaDict = computed<Record<string, any> | null>(() => {
    const s = this.variablesSchema();
    if (!s?.properties?.length) return null;
    const dict = descriptorEntriesToDict(s.properties);
    // system__* — служебные, юзеру не показываем: их подставит бэк.
    const filtered: Record<string, any> = {};
    for (const [k, v] of Object.entries(dict)) {
      if (!k.startsWith('system__')) filtered[k] = v;
    }
    return Object.keys(filtered).length ? filtered : null;
  });
  readonly variableRequiredKeys = computed<string[]>(() => {
    const list = this.variablesSchema()?.required ?? [];
    return list.filter(k => !k.startsWith('system__'));
  });

  // Свежесозданный звонок — рисуем строкой под инпутами. Обновляется по
  // SignalR (agentSessionUpdated $.filter(call_sid match)).
  readonly lastCall = signal<CallDto | null>(null);
  private lastCallWatch$: Subject<void> | null = null;

  readonly callStatusOptions: { label: string; value: CallStatus }[] = [
    { label: $localize`:@@conversations.callStatus.ringing:Ringing`, value: 'Ringing' },
    { label: $localize`:@@conversations.callStatus.answered:Answered`, value: 'Answered' },
    { label: $localize`:@@conversations.callStatus.completed:Completed`, value: 'Completed' },
    { label: $localize`:@@conversations.callStatus.notAnswered:Not answered`, value: 'NotAnswered' },
  ];
  private readonly endReasonLabels: Record<EndReason, string> = {
    ClientHangup: $localize`:@@conversations.endReason.clientHangup:Client hung up`,
    AgentHangup: $localize`:@@conversations.endReason.agentHangup:Agent hung up`,
    Transferred: $localize`:@@conversations.endReason.transferred:Transferred`,
    Failed: $localize`:@@conversations.endReason.failed:Failed`,
    NoAnswer: $localize`:@@conversations.endReason.noAnswer:No answer`,
    Busy: $localize`:@@conversations.endReason.busy:Busy`,
    Timeout: $localize`:@@conversations.endReason.timeout:Timeout`,
    AnsweringMachine: $localize`:@@conversations.endReason.answeringMachine:Answering machine`,
  };
  readonly unknownStatusLabel = $localize`:@@conversations.status.unknown:Unknown`;

  breadcrumbItems: MenuItem[] = [];
  menuItems: MenuItem[] = [];

  readonly phoneNumberFallback = $localize`:@@phoneNumberEdit.fallback:Phone Number`;
  readonly deleteTitle = $localize`:@@phoneNumbers.delete.title:Delete Phone Number`;
  get deleteSubtitle(): string {
    const number = this.phoneNumberAgent?.phone_number || '';
    return $localize`:@@phoneNumbers.delete.subtitle:Are you sure you want to delete phone number '${number}:number:'? This action cannot be undone.`;
  }
  get saveBtnLabel(): string {
    return this.isSaving
      ? $localize`:@@common.saving:Saving...`
      : $localize`:@@common.save:Save`;
  }

  ngOnInit(): void {
    const phoneNumberAgentId = this.route.snapshot.paramMap.get('phoneNumberAgentId');
    if (phoneNumberAgentId) {
      this.loadPhoneNumberAgent(phoneNumberAgentId);
      this.loadAgents();
    }
  }

  private loadPhoneNumberAgent(id: string): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomPhoneNumberAgentsIdGet({ id }).subscribe({
      next: (phoneNumberAgent: PhoneNumberAgentResult) => {
        this.phoneNumberAgent = phoneNumberAgent;
        this.selectedAgentId = phoneNumberAgent.agent_id || null;
        this.sipHeaderVariablePrefix.set(phoneNumberAgent.sip_header_variable_prefix ?? '');
        this.loadProvider();
        this.loadAgentVariablesSchema();
        this.updateBreadcrumbs();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Failed to load phone number:', error);
        this.notificationService.showError($localize`:@@phoneNumberEdit.notify.loadFail:Failed to load phone number`);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadProvider(): void {
    if (!this.phoneNumberAgent?.telecom_account_id) return;

    this.telecomService.apiTelecomAccountsIdGet({ id: this.phoneNumberAgent.telecom_account_id }).subscribe({
      next: (account) => {
        this.provider = account.provider || '';
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Failed to load provider:', error);
      }
    });
  }

  private loadAgents(): void {
    this.isLoadingAgents = true;
    this.agentsLoadError = false;
    this.cdr.markForCheck();

    this.agentsService.apiAgentsGet().subscribe({
      next: (result) => {
        this.agents = result.agents || [];
        this.isLoadingAgents = false;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Failed to load agents:', error);
        this.notificationService.showError($localize`:@@conversations.notify.agentsFail:Failed to load agents`);
        this.agentsLoadError = true;
        this.isLoadingAgents = false;
        this.cdr.markForCheck();
      }
    });
  }

  reloadAgents(): void {
    this.loadAgents();
  }

  /** Юзер сменил агента в дропдауне — перезагружаем его variables-схему,
   *  сбрасываем значения формы (валидность пересчитается автоматом). */
  onAgentChange(): void {
    this.variableValues.set({});
    this.loadAgentVariablesSchema();
  }

  private loadAgentVariablesSchema(): void {
    const agentId = this.selectedAgentId;
    if (!agentId) {
      this.variablesSchema.set(null);
      return;
    }
    this.agentsService.apiAgentsIdGet({ id: agentId }).subscribe({
      next: (agent) => {
        this.variablesSchema.set(agent?.variables_open_api_schema ?? null);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load agent variables schema:', error);
        this.variablesSchema.set(null);
        this.cdr.markForCheck();
      },
    });
  }

  onVariableValuesChange(values: Record<string, any>): void {
    this.variableValues.set(values);
  }

  /** true → можно кликать «Позвонить». Тултип объясняет причину при false. */
  get canPlaceCall(): boolean {
    if (this.isTestingCall) return false;
    if (!this.selectedAgentId) return false;
    if (!this.testCallTargetNumber?.trim()) return false;
    if (this.variableSchemaDict() && !this.variablesValid()) return false;
    return true;
  }

  get placeCallTooltip(): string {
    if (!this.selectedAgentId) return $localize`:@@phoneNumberEdit.testCall.needAgent:Select an agent first`;
    if (!this.testCallTargetNumber?.trim()) return $localize`:@@phoneNumberEdit.testCall.needNumber:Enter a target phone number`;
    if (this.variableSchemaDict() && !this.variablesValid()) {
      return $localize`:@@agentTest.variables.invalidTooltip:Please fill in the required variables to start the call.`;
    }
    return '';
  }

  getCallStatusLabel(status: CallStatus | null | undefined): string {
    if (!status) return this.unknownStatusLabel;
    return this.callStatusOptions.find(o => o.value === status)?.label ?? status;
  }
  getCallStatusSeverity(status: CallStatus | null | undefined): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    switch (status) {
      case 'Completed': return 'success';
      case 'Answered': return 'info';
      case 'Ringing': return 'warn';
      case 'NotAnswered': return 'danger';
      default: return 'secondary';
    }
  }
  getEndReasonLabel(r: EndReason | null | undefined): string {
    return r ? this.endReasonLabels[r] : '-';
  }
  getEndReasonSeverity(r: EndReason | null | undefined): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    switch (r) {
      case 'Transferred': return 'info';
      case 'Failed': return 'danger';
      case 'NoAnswer':
      case 'Busy':
      case 'Timeout': return 'warn';
      default: return 'secondary';
    }
  }
  formatDuration(seconds: number | null | undefined): string {
    if (!seconds) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  private updateBreadcrumbs(): void {
    this.breadcrumbItems = [
      {
        label: $localize`:@@nav.phoneNumbers:Phone Numbers`,
        routerLink: '/phone-numbers'
      },
      {
        label: this.phoneNumberAgent?.phone_number || this.phoneNumberFallback
      }
    ];

    this.menuItems = [
      {
        label: $localize`:@@phoneNumberEdit.menu.editLabel:Edit label`,
        icon: 'pi pi-pencil',
        command: () => this.startEditingLabel()
      },
      {
        label: $localize`:@@phoneNumberEdit.menu.copy:Copy phone number`,
        icon: 'pi pi-copy',
        command: () => this.copyPhoneNumber()
      },
      {
        label: $localize`:@@phoneNumberEdit.menu.delete:Delete phone number`,
        icon: 'pi pi-trash',
        command: () => this.deletePhoneNumber()
      }
    ];
  }

  copyPhoneNumber(): void {
    if (this.phoneNumberAgent?.phone_number) {
      navigator.clipboard.writeText(this.phoneNumberAgent.phone_number).then(() => {
        this.notificationService.showSuccess($localize`:@@phoneNumbers.notify.copied:Phone number copied`);
      }).catch((error) => {
        console.error('Failed to copy phone number:', error);
        this.notificationService.showError($localize`:@@phoneNumbers.notify.copyFail:Failed to copy phone number`);
      });
    }
  }

  deletePhoneNumber(): void {
    this.deleteDialogVisible = true;
    this.cdr.markForCheck();
  }

  copyId(): void {
    if (this.phoneNumberAgent?.id) {
      navigator.clipboard.writeText(this.phoneNumberAgent.id).then(() => {
        this.notificationService.showSuccess($localize`:@@common.idCopied:ID copied`);
      }).catch((error) => {
        console.error('Failed to copy ID:', error);
        this.notificationService.showError($localize`:@@common.idCopyFail:Failed to copy ID`);
      });
    }
  }

  onDeleteConfirm(): void {
    if (!this.phoneNumberAgent?.id) return;

    this.isDeleting = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomPhoneNumberAgentsIdDelete({ id: this.phoneNumberAgent.id }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@phoneNumbers.notify.deleted:Phone number deleted`);
        this.isDeleting = false;
        this.deleteDialogVisible = false;
        this.goBack();
      },
      error: (error: any) => {
        console.error('Failed to delete phone number:', error);
        this.notificationService.showError($localize`:@@phoneNumbers.notify.deleteFail:Failed to delete phone number`);
        this.isDeleting = false;
        this.cdr.markForCheck();
      }
    });
  }

  onDeleteCancel(): void {
    this.deleteDialogVisible = false;
    this.cdr.markForCheck();
  }

  goBack(): void {
    this.router.navigate(['/phone-numbers']);
  }

  startEditingLabel(): void {
    this.editedLabel = this.phoneNumberAgent?.label || '';
    this.isEditingLabel = true;
    this.cdr.markForCheck();
  }

  saveLabel(): void {
    if (!this.phoneNumberAgent) return;

    this.phoneNumberAgent.label = this.editedLabel;
    this.isEditingLabel = false;
    this.cdr.markForCheck();
  }

  cancelEditLabel(): void {
    this.isEditingLabel = false;
    this.cdr.markForCheck();
  }

  /**
   * Префикс SIP-заголовков входящего звонка, которые становятся переменными
   * агента (SKN-438): "X-" — заголовок X-minimumAmount заполнит переменную
   * minimumAmount. Пусто — выключено.
   */
  readonly sipHeaderVariablePrefix = signal('');

  save(): void {
    if (!this.phoneNumberAgent?.id) return;

    this.isSaving = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomPhoneNumberAgentsIdPatch({
      id: this.phoneNumberAgent.id,
      body: {
        agent_id: this.selectedAgentId || null,
        label: this.phoneNumberAgent.label || null,
        // Пустая строка — выключить маппинг (null бэк трактует как «не трогать»).
        sip_header_variable_prefix: this.sipHeaderVariablePrefix().trim()
      }
    }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@phoneNumberEdit.notify.updated:Phone number updated`);
        this.isSaving = false;
        this.goBack();
      },
      error: (error: any) => {
        console.error('Failed to update phone number:', error);
        this.notificationService.showError($localize`:@@phoneNumberEdit.notify.updateFail:Failed to update phone number`);
        this.isSaving = false;
        this.cdr.markForCheck();
      }
    });
  }

  get agentOptions() {
    return this.agents.map(agent => ({
      label: agent.title || agent.id,
      value: agent.id
    }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  placeCall(): void {
    if (!this.canPlaceCall || !this.phoneNumberAgent?.id) return;

    this.isTestingCall = true;
    this.lastCall.set(null);
    this.stopLastCallWatch();
    this.cdr.markForCheck();

    const variables = this.variableSchemaDict()
      ? dictToValueEntries(this.filteredVariableValues())
      : null;

    this.telecomService.apiTelecomOutboundCallPost({
      body: {
        agent_id: this.selectedAgentId!,
        phone_number_agent_id: this.phoneNumberAgent.id,
        to_phone_number: this.testCallTargetNumber,
        variables: variables && variables.length > 0 ? variables : null,
      }
    }).subscribe({
      next: (result) => {
        if (result.success && result.call_id) {
          this.notificationService.showSuccess($localize`:@@phoneNumberEdit.notify.testCallStarted:Test call initiated`);
          this.fetchAndWatchCall(result.call_id);
        } else {
          this.notificationService.showError(result.error || $localize`:@@phoneNumberEdit.notify.testCallFail:Failed to initiate test call`);
        }
        this.isTestingCall = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to initiate test call:', error);
        this.notificationService.showError($localize`:@@phoneNumberEdit.notify.testCallFail:Failed to initiate test call`);
        this.isTestingCall = false;
        this.cdr.markForCheck();
      }
    });
  }

  /** Отправляем в бэк только те values, что есть в актуальной схеме
   *  (пользователь мог поменять агента, стейл-ключи не шлём). */
  private filteredVariableValues(): Record<string, any> {
    const schemaDict = this.variableSchemaDict();
    if (!schemaDict) return {};
    const values = this.variableValues();
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(values)) {
      if (schemaDict[k] !== undefined) out[k] = v;
    }
    return out;
  }

  /** GET /api/Telecom/calls/{id} — рендерим строку. Подписываемся на
   *  agentSessionUpdated: любая новость по этому call_sid → рефетч, чтобы
   *  обновились status/end_reason/duration и agent_session_id (Live-кнопка). */
  private fetchAndWatchCall(callId: string): void {
    this.telecomService.apiTelecomCallsIdGet({ id: callId }).subscribe({
      next: (result) => {
        if (result.data) {
          this.lastCall.set(result.data);
          this.startLastCallWatch(result.data);
          this.cdr.markForCheck();
        }
      },
      error: (error) => console.error('Failed to fetch call:', error),
    });
  }

  private startLastCallWatch(call: CallDto): void {
    this.stopLastCallWatch();
    const callSid = call.call_sid;
    if (!callSid || !call.id) return;
    this.lastCallWatch$ = new Subject<void>();
    this.signalrService.agentSessionUpdated$
      .pipe(takeUntil(this.lastCallWatch$), takeUntil(this.destroy$))
      .subscribe((session) => {
        if (session.call_sid !== callSid) return;
        // Прилетело обновление по нашему звонку — перечитываем целиком.
        this.telecomService.apiTelecomCallsIdGet({ id: call.id! }).subscribe({
          next: (r) => {
            if (r.data) {
              this.lastCall.set(r.data);
              this.cdr.markForCheck();
            }
          },
          error: () => {},
        });
      });
  }

  private stopLastCallWatch(): void {
    if (this.lastCallWatch$) {
      this.lastCallWatch$.next();
      this.lastCallWatch$.complete();
      this.lastCallWatch$ = null;
    }
  }
}
